import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { useSite } from "@/lib/SiteContext";
import { assetUrl } from "@/lib/api";
import { ArrowRight } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const CATHEDRAL_FIGURE =
  "https://images.pexels.com/photos/5944992/pexels-photo-5944992.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Author() {
  const { settings } = useSite();
  const name = settings.author_name || "Jermaine Miller";
  const fullName = settings.author_full_name || "Jermaine Irvin Darrell Miller";

  return (
    <div className="bg-ink" data-testid="author-page">
      <Seo
        title={`${name} — Author of Dirty Church Chronicles`}
        description={`${name} (${fullName}) is the author of Dirty Church Chronicles and founder and pastor of Dirty Church Kingdom House.`}
        path="/author"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: fullName,
          alternateName: name,
          jobTitle: "Author",
        }}
      />
      <section className="relative min-h-[85vh] flex items-end pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={CATHEDRAL_FIGURE} alt="" className="w-full h-full object-cover opacity-20" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <Reveal className="lg:col-span-4">
            {settings.author_portrait_url ? (
              <img src={assetUrl(settings.author_portrait_url)} alt={`Portrait of ${name}`}
                className="w-full aspect-[3/4] object-cover shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]" data-testid="author-portrait" />
            ) : (
              <div className="w-full aspect-[3/4] bg-coal border border-paper/10 flex flex-col items-center justify-center gap-6" data-testid="author-portrait">
                <LogoMark size={64} />
                <span className="font-display font-black text-7xl text-paper/15 tracking-tight">JM</span>
              </div>
            )}
          </Reveal>
          <div className="lg:col-span-8">
            <Reveal>
              <p className="eyebrow mb-5">The Author</p>
              <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight" data-testid="author-name">
                {name}
              </h1>
              <p className="font-reading italic text-xl text-gold/90 mt-4">{fullName}</p>
              <p className="font-ui text-xs uppercase tracking-[0.25em] text-crimsonhi mt-3" data-testid="author-role">
                {settings.author_role || "Author · Founder & Pastor, Dirty Church Kingdom House"}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="paper-section py-24 sm:py-32" data-testid="author-bio-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-10">
          <Reveal>
            <p className="eyebrow eyebrow-light mb-8">Biography</p>
            <p className="font-reading text-2xl sm:text-3xl leading-relaxed text-ink/85" data-testid="author-bio">
              {settings.author_bio}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border-l-2 border-crimson pl-8 mt-14">
              <p className="font-reading text-xl leading-relaxed text-ink/70">
                Dirty Church Chronicles is written from the conviction that the Church is called to honesty before
                God — to examine, to repent, and to return.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.25} className="flex flex-wrap gap-4 mt-14">
            <Link to="/connect?category=speaking" data-testid="author-speaking-btn"
              className="inline-flex items-center gap-3 font-ui text-xs uppercase tracking-[0.25em] font-semibold bg-ink text-paper px-8 py-4 rounded-full transition-[background-color,transform] duration-300 hover:bg-crimson hover:scale-[1.02] active:scale-[0.98]">
              Speaking Inquiries <ArrowRight size={14} />
            </Link>
            <Link to="/connect?category=media" data-testid="author-media-btn"
              className="inline-flex items-center gap-3 font-ui text-xs uppercase tracking-[0.25em] text-ink border border-ink/30 px-8 py-4 rounded-full transition-colors duration-300 hover:border-ink">
              Media & Press
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
